/**
 * ShopSelector — pill + dropdown shop FILTER. "All shops" (null) is the
 * default and always available; picking a shop narrows what the pipeline
 * shows, it never restricts what the user may see (CLAUDE.md decision 3).
 * .shop-pill + .dropdown-shadow are defined in src/index.css.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useShopStore, useShops, type ShopOption } from '@/lib/shop-store';
import { ALL_SHOPS_META, shopMetaFor, type ShopMeta } from '@/lib/shop-colors';

const ALL_SHOPS: ShopOption = { id: '', name: 'All shops' };

/** Decorative letter disc — the shop name follows it, so it is hidden from the accessibility tree. */
function Avatar({ meta }: { meta: ShopMeta }) {
  return (
    <span
      aria-hidden
      className={`w-7 h-7 rounded-full bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-[12px] font-semibold text-white shrink-0`}
    >
      {meta.letter}
    </span>
  );
}

function metaOf(shop: ShopOption): ShopMeta {
  return shop.id ? shopMetaFor(shop.id, shop.name) : ALL_SHOPS_META;
}

export function ShopSelector() {
  const { data: shops, isPending } = useShops();
  const selectedId = useShopStore((s) => s.selectedShopId);
  const setShop = useShopStore((s) => s.setShop);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const options: ShopOption[] = [ALL_SHOPS, ...(shops ?? [])];
  const selected = options.find((s) => s.id === (selectedId ?? '')) ?? ALL_SHOPS;
  const selMeta = metaOf(selected);

  // Listbox keyboard support: focus the selected option on open; ArrowUp /
  // ArrowDown / Home / End move, Escape closes and returns focus to the pill.
  useEffect(() => {
    if (!open) return;
    const optionEls = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    const focusAt = (i: number) => {
      const els = optionEls();
      els[(i + els.length) % els.length]?.focus();
    };
    const raf = requestAnimationFrame(() => {
      const els = optionEls();
      const idx = els.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      focusAt(idx < 0 ? 0 : idx);
    });
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      const els = optionEls();
      const idx = els.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(idx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); focusAt(idx - 1); }
      else if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
      else if (e.key === 'End') { e.preventDefault(); focusAt(els.length - 1); }
      else if (e.key === 'Tab') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by shop"
        className="shop-pill flex min-h-10 items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full text-[13px] font-medium text-gray-800 transition-all"
      >
        <Avatar meta={selMeta} />
        <span className="truncate max-w-[9rem] sm:max-w-none">{selected.name}</span>
        <ChevronsUpDown size={14} strokeWidth={2} className="text-gray-500" aria-hidden />
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Shops"
          className="absolute top-[46px] left-0 w-[280px] max-w-[calc(100vw-1.5rem)] bg-white rounded-xl dropdown-shadow z-50 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-[12px] font-semibold text-muted">Filter by shop</div>
          </div>
          <div className="p-1.5 max-h-[60vh] overflow-y-auto scrollbar-thin">
            {options.map((shop) => {
              const meta = metaOf(shop);
              const isSelected = shop.id === (selectedId ?? '');
              return (
                <button
                  key={shop.id || 'all'}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  onClick={() => {
                    setShop(shop.id || null);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  className={[
                    'w-full flex min-h-10 items-center gap-2.5 px-2.5 py-2 rounded-lg',
                    isSelected ? 'bg-page' : 'hover:bg-surface',
                  ].join(' ')}
                >
                  <Avatar meta={meta} />
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 truncate">{shop.name}</div>
                    {!shop.id && (
                      <div className="text-[12px] text-muted">Every shop in your company</div>
                    )}
                  </div>
                  {isSelected && <Check size={14} strokeWidth={2.5} className="text-navy" aria-hidden />}
                </button>
              );
            })}
            {isPending && (
              <div className="px-2.5 py-2 text-[12px] text-muted">Loading shops…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
